import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { useSearchParams, useLocation } from 'react-router-dom';
import api from '../services/authService';
import { Candidate, Pagination as PaginationType } from '../types';
import { colors } from '../styles/colors';
import CandidateForm from './CandidateForm';
import CandidateProfileForm from './CandidateProfileForm';
import CandidateDetail from './CandidateDetail';
import CandidateSubscriptionsModal from './CandidateSubscriptionsModal';
import Pagination from './Pagination';
import ConfirmModal from './ConfirmModal';
import { useDebounce, useAbortController } from '../hooks/useDebounce';

const Candidates: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const skipNextFetch = useRef(false);
  const isInitialMount = useRef(true);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [pagination, setPagination] = useState<PaginationType>({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('email');
  const [scope, setScope] = useState('all');

  const formatCPF = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 11) {
      return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    return numbers.slice(0, 11).replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (searchType === 'cpf') {
      setSearch(formatCPF(value));
    } else {
      setSearch(value);
    }
  };
  const [showForm, setShowForm] = useState(false);
  const [editingCandidate, setEditingCandidate] = useState<Candidate | undefined>();
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [editingCandidateId, setEditingCandidateId] = useState<number | undefined>();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [candidateToDelete, setCandidateToDelete] = useState<Candidate | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [detailCandidateId, setDetailCandidateId] = useState<number | undefined>();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showSubscriptions, setShowSubscriptions] = useState(false);
  const [subscriptionsCandidateId, setSubscriptionsCandidateId] = useState<number | undefined>();
  const [openActionMenu, setOpenActionMenu] = useState<number | null>(null);

  useEffect(() => {
    const candidateId = searchParams.get('candidateId');
    if (candidateId) {
      skipNextFetch.current = true;
      setSearchParams({});
      setSearchType('id');
      setSearch(candidateId);
      setSearchTerm(candidateId);
      setPagination(prev => ({ ...prev, page: 1 }));
      
      const fetchDirect = async () => {
        try {
          setLoading(true);
          const params = new URLSearchParams({
            page: '1',
            limit: pagination.limit.toString(),
            search: candidateId,
            searchType: 'id',
          });
          const response = await api.get(`/candidates?${params}`);
          setCandidates(response.data.candidates);
          setPagination(response.data.pagination);
        } catch (error: any) {
          toast.error('Erro ao carregar candidatos');
        } finally {
          setLoading(false);
        }
      };
      fetchDirect();
    }
  }, [location.search]);

  useEffect(() => {
    if (skipNextFetch.current) {
      skipNextFetch.current = false;
      return;
    }
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (!searchParams.get('candidateId')) {
        fetchCandidates();
      }
      return;
    }
    fetchCandidates();
  }, [pagination.page, searchTerm, scope]);

  const fetchCandidates = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        scope,
        ...(searchTerm && { search: searchTerm, searchType }),
      });

      const response = await api.get(`/candidates?${params}`);
      setCandidates(response.data.candidates);
      setPagination(response.data.pagination);
    } catch (error: any) {
      toast.error('Erro ao carregar candidatos');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Envia com máscara pois o banco tem com máscara
    setSearchTerm(search);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleScopeChange = (newScope: string) => {
    setScope(newScope);
    setSearch('');
    setSearchTerm('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSearchTypeChange = (newType: string) => {
    setSearchType(newType);
    setSearch('');
    setSearchTerm('');
  };

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleCreate = () => {
    setEditingCandidate(undefined);
    setShowForm(true);
  };

  const handleEdit = (candidate: Candidate) => {
    setEditingCandidate(candidate);
    setShowForm(true);
  };

  const handleEditProfile = (candidate: Candidate) => {
    setEditingCandidateId(candidate.id);
    setShowProfileForm(true);
  };

  const handleDelete = (candidate: Candidate) => {
    setCandidateToDelete(candidate);
    setShowConfirmModal(true);
  };

  const handleView = (candidate: Candidate) => {
    setDetailCandidateId(candidate.id);
    setShowDetail(true);
  };

  const handleCloseDetail = () => {
    setShowDetail(false);
    setDetailCandidateId(undefined);
  };

  const handleChangePassword = async (candidate: Candidate) => {
    try {
      const response = await api.put(`/candidates/${candidate.id}`, { change_password: true });
      if (response.data.new_password) {
        setNewPassword(response.data.new_password);
        setShowPasswordModal(true);
      }
    } catch (error: any) {
      toast.error('Erro ao gerar nova senha');
    }
  };

  const handleViewSubscriptions = (candidate: Candidate) => {
    setSubscriptionsCandidateId(candidate.id);
    setShowSubscriptions(true);
  };

  const handleCloseSubscriptions = () => {
    setShowSubscriptions(false);
    setSubscriptionsCandidateId(undefined);
  };

  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
    setNewPassword('');
  };

  const toggleActionMenu = (candidateId: number) => {
    setOpenActionMenu(openActionMenu === candidateId ? null : candidateId);
  };

  const confirmDelete = async () => {
    if (candidateToDelete) {
      try {
        await api.delete(`/candidates/${candidateToDelete.id}`);
        toast.success('Candidato excluído com sucesso!');
        fetchCandidates();
      } catch (error: any) {
        toast.error('Erro ao excluir candidato');
      }
    }
    setShowConfirmModal(false);
    setCandidateToDelete(null);
  };

  const cancelDelete = () => {
    setShowConfirmModal(false);
    setCandidateToDelete(null);
  };

  const handleFormSave = () => {
    setShowForm(false);
    setEditingCandidate(undefined);
    fetchCandidates();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingCandidate(undefined);
  };

  const handleProfileFormSave = () => {
    setShowProfileForm(false);
    setEditingCandidateId(undefined);
    fetchCandidates();
  };

  const handleProfileFormCancel = () => {
    setShowProfileForm(false);
    setEditingCandidateId(undefined);
  };

  return (
    <div style={styles.container} onClick={() => setOpenActionMenu(null)}>
      <div style={styles.header}>
        <h1 style={styles.title}>Candidatos</h1>
      </div>
      
      <div style={styles.scopes}>
        <button 
          onClick={() => handleScopeChange('all')} 
          style={{...styles.scopeBtn, ...(scope === 'all' ? styles.scopeBtnActive : {})}}
        >
          Todos
        </button>
        <button 
          onClick={() => handleScopeChange('premium')} 
          style={{...styles.scopeBtn, ...(scope === 'premium' ? styles.scopeBtnActive : {})}}
        >
          Premium
        </button>
        <button 
          onClick={() => handleScopeChange('regular')} 
          style={{...styles.scopeBtn, ...(scope === 'regular' ? styles.scopeBtnActive : {})}}
        >
          Não Premium
        </button>
      </div>
      
      <form onSubmit={handleSearch} style={styles.filters}>
        <select
          value={searchType}
          onChange={(e) => handleSearchTypeChange(e.target.value)}
          style={styles.select}
        >
          <option value="email">Email</option>
          <option value="cpf">CPF</option>
          <option value="name">Nome</option>
          <option value="id">ID</option>
        </select>
        <input
          type="text"
          placeholder={`Buscar por ${searchType === 'email' ? 'email' : searchType === 'cpf' ? 'CPF' : searchType === 'name' ? 'nome' : 'ID'}...`}
          value={search}
          onChange={handleSearchChange}
          style={styles.input}
          maxLength={searchType === 'cpf' ? 14 : undefined}
        />
        <button type="submit" style={styles.searchBtn}>
          Buscar
        </button>
      </form>

      {loading ? (
        <div style={styles.loading}>Carregando...</div>
      ) : (
        <>
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.tableHeader}>
                  <th style={styles.th}>ID</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Nome</th>
                  <th style={styles.th}>CPF</th>
                  <th style={styles.th}>Confirmado</th>
                  <th style={styles.th}>Criado em</th>
                  {scope === 'deleted' && (
                    <th style={styles.th}>Motivo da Exclusão</th>
                  )}
                  <th style={styles.th}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {candidates.map((candidate) => (
                  <tr key={candidate.id} style={styles.tableRow}>
                    <td style={styles.td}>{candidate.id}</td>
                    <td style={styles.td}>{candidate.email}</td>
                    <td style={styles.td}>{candidate.full_name || '-'}</td>
                    <td style={styles.td}>{candidate.document_number || '-'}</td>
                    <td style={{ padding: '12px' }}>{candidate.is_confirmed !== undefined ? (
                      candidate.is_confirmed ? (
                        <span style={styles.confirmedBadge}>SIM</span>
                      ) : (
                        <span style={styles.unconfirmedBadge}>NÃO</span>
                      )
                    ) : '-'}</td>
                    <td style={styles.td}>
                      {new Date(candidate.created_at).toLocaleDateString('pt-BR')}
                    </td>
                    {scope === 'deleted' && (
                      <td style={styles.td}>{candidate.deleted_reason || '-'}</td>
                    )}
                    <td style={styles.tdActions}>
                      <div style={styles.actionMenuContainer}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleActionMenu(candidate.id);
                          }}
                          style={styles.actionMenuBtn}
                          title="Ações"
                        >
                          ⋮
                        </button>
                        {openActionMenu === candidate.id && (
                          <div style={styles.actionMenu}>
                            <button
                              onClick={() => {
                                handleView(candidate);
                                setOpenActionMenu(null);
                              }}
                              style={styles.actionMenuItem}
                            >
                              Ver Detalhes
                            </button>
                            <button
                              onClick={() => {
                                handleEdit(candidate);
                                setOpenActionMenu(null);
                              }}
                              style={styles.actionMenuItem}
                            >
                              Editar Cadastro
                            </button>
                            <button
                              onClick={() => {
                                handleChangePassword(candidate);
                                setOpenActionMenu(null);
                              }}
                              style={styles.actionMenuItem}
                            >
                              Alterar Senha
                            </button>
                            {candidate.candidateProfile && (
                              <button
                                onClick={() => {
                                  handleEditProfile(candidate);
                                  setOpenActionMenu(null);
                                }}
                                style={styles.actionMenuItem}
                              >
                                Editar Perfil
                              </button>
                            )}
                            <button
                              onClick={() => {
                                handleViewSubscriptions(candidate);
                                setOpenActionMenu(null);
                              }}
                              style={styles.actionMenuItem}
                            >
                              Ver Assinaturas
                            </button>
                            {scope !== 'deleted' && (
                              <button
                                onClick={() => {
                                  handleDelete(candidate);
                                  setOpenActionMenu(null);
                                }}
                                style={{...styles.actionMenuItem, color: colors.error}}
                              >
                                Excluir
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={styles.paginationContainer}>
            <div style={styles.paginationInfo}>
              Página {pagination.page} de {pagination.totalPages} ({pagination.total} total)
            </div>
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </>
      )}
      
      {showForm && (
        <CandidateForm
          candidate={editingCandidate}
          onSave={handleFormSave}
          onCancel={handleFormCancel}
        />
      )}
      
      {showProfileForm && editingCandidateId && (
        <CandidateProfileForm
          candidateId={editingCandidateId}
          onSave={handleProfileFormSave}
          onCancel={handleProfileFormCancel}
        />
      )}
      
      <ConfirmModal
        isOpen={showConfirmModal}
        title="Confirmar Exclusão"
        message={`Tem certeza que deseja excluir o candidato ${candidateToDelete?.email}?`}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        confirmText="Excluir"
        cancelText="Cancelar"
      />
      
      {showDetail && detailCandidateId && (
        <CandidateDetail
          candidateId={detailCandidateId}
          onClose={handleCloseDetail}
        />
      )}
      
      {showSubscriptions && subscriptionsCandidateId && (
        <CandidateSubscriptionsModal
          candidateId={subscriptionsCandidateId}
          onClose={handleCloseSubscriptions}
        />
      )}
      
      <ConfirmModal
        isOpen={showPasswordModal}
        title="Nova Senha Gerada"
        message={`Nova senha para o candidato: ${newPassword}`}
        onConfirm={handleClosePasswordModal}
        confirmText="OK"
        showCancel={false}
      />
    </div>
  );
};

const styles = {
  container: {
    width: '100%',
  },
  title: {
    fontSize: '2rem',
    color: colors.primary,
    marginBottom: '2rem',
  },
  filters: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '2rem',
    flexWrap: 'wrap' as const,
    alignItems: 'center',
  },
  select: {
    padding: '0.75rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '1rem',
    minWidth: '120px',
    backgroundColor: colors.white,
  },
  input: {
    padding: '0.75rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '1rem',
    minWidth: '400px',
  },
  searchBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '1rem',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '2rem',
    fontSize: '1.2rem',
    color: colors.textLight,
  },
  tableContainer: {
    backgroundColor: colors.white,
    borderRadius: '8px',
    overflow: 'visible' as const,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    minWidth: '1200px',
    overflow: 'visible' as const,
  },
  tableHeader: {
    backgroundColor: colors.primary,
    color: colors.white,
  },
  th: {
    padding: '1rem',
    textAlign: 'left' as const,
    fontWeight: '600',
  },
  tableRow: {
    borderBottom: `1px solid ${colors.border}`,
  },
  td: {
    padding: '1rem',
    color: colors.text,
  },
  paginationContainer: {
    marginTop: '2rem',
  },
  paginationInfo: {
    textAlign: 'center' as const,
    color: colors.textLight,
    marginBottom: '1rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2rem',
  },
  createBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: colors.secondary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '1rem',
  },
  iconBtn: {
    padding: '0.5rem',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    marginRight: '0.25rem',
    fontSize: '1.2rem',
    transition: 'background-color 0.2s',
    color: colors.primary,
  },
  scopes: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1rem',
  },
  scopeBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: colors.white,
    color: colors.text,
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '0.9rem',
  },
  scopeBtnActive: {
    backgroundColor: colors.primary,
    color: colors.white,
    borderColor: colors.primary,
  },
  premiumBadge: {
    backgroundColor: colors.secondary,
    color: colors.white,
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
  },
  tdActions: {
    padding: '1rem',
    color: colors.text,
    minWidth: '80px',
    position: 'relative' as const,
  },
  actionMenuContainer: {
    position: 'relative' as const,
  },
  actionMenuBtn: {
    padding: '8px 12px',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold',
  },
  actionMenu: {
    position: 'absolute' as const,
    top: '100%',
    right: 0,
    backgroundColor: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    zIndex: 1001,
    minWidth: '180px',
  },
  actionMenuItem: {
    display: 'block',
    width: '100%',
    padding: '12px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    textAlign: 'left' as const,
    cursor: 'pointer',
    fontSize: '14px',
    color: colors.text,
    borderBottom: `1px solid ${colors.border}`,
    fontFamily: 'inherit',
  },
  confirmedBadge: {
    backgroundColor: colors.success,
    color: colors.white,
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
  },
  unconfirmedBadge: {
    backgroundColor: colors.warning,
    color: colors.white,
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
  },
};

export default Candidates;